/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import debounce from 'lodash.debounce';
import {
  useDispatch, useSelector, shallowEqual,
} from 'react-redux';
import { useParams } from 'react-router-dom';
import { eventNames } from '@ziynx/analytics';
import { ContentContainer } from '@ziynx/components/layout';
import Card from '@ziynx/components/cards';
import Link from '@ziynx/components/links';
import { Heading } from '@ziynx/components/typography';
import Colors from '@ziynx/theme/colors';
import { Form, FormRow } from '@ziynx/components/forms';
import Modal from '@ziynx/components/modals';
import Input, { InputAutocomplete } from '@ziynx/components/inputs';
import type { ImageUploadConfig } from '@ziynx/flow-types/companyProfile';
import UploadPhotoModal from '@ziynx/pages/Modals/UploadPhoto';
import DestructiveModal from '@ziynx/pages/Modals/DestructiveModal';
import { Button } from '@ziynx/components/buttons';
import ActionTypes from '@ziynx/action-types';
import {
  withAppManager, useNotification, LoadingSpinner,
  dateToUnixTimestamp, unixTimestampToDate,
} from '@ziynx/components/utilities';
import * as KrispyTokens from '@krispy-tokens/blue';

type Props = $ReadOnly<{|
  /**
   * Viewport
   */
  viewport: Viewports,
  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string, params?: { from: string }) => void,
    goBack: () => void,
    location: {
      state: {
        from: string,
      }
    },
  },

|}>;

const _modalWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '333px';
  case 'mobileSmall':
    return '240px';
  default:
    return '488px';
  }
};

const modalButtonWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '50%';
  case 'mobileSmall':
    return '100%';
  default:
    return '40%';
  }
};

const DiscardModal = styled(Modal)`
  padding: 24px;
  height: 180px;
  width: ${({ viewport }) => _modalWidth(viewport)};
`;

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
`;

const ModalButton = styled(Button)`
  width: ${({ viewport }) => modalButtonWidth(viewport)};
  ${({ isMobileSmall, firstChild }) => !isMobileSmall && firstChild && 'margin-right: 16px;'}
  ${({ isMobileSmall, firstChild }) => isMobileSmall && firstChild && 'margin-bottom: 16px;'}
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

const StyledHeading = styled(Heading)`
  padding-bottom: 16px;
`;

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  ${({ isMobileSmall }) => (isMobileSmall
    ? `padding-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px; 
      padding-right: ${KrispyTokens.KrispySpacingElementsHdXlarge}px; 
      padding-bottom${KrispyTokens.KrispySpacingElementsLdLarge}px;
      padding-left: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`
    : `paddding-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px; 
      padding-right: ${KrispyTokens.KrispySpacingElementsLdXsmall}px; 
      paddding-bottom: ${KrispyTokens.KrispySpacingElementsLdLarge}px;
      padding-left: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;`)};
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
  form h3 {
    color: ${Colors.GrayVeryDark}
  }
  fieldset {
    all: unset;
    width: 100%;
  }
`;

const LinkWrapper = styled.div`
  margin-top: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
  margin-bottom: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
  margin-bottom: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
`;

const StyledButton = styled(Button)`
  width: ${({ isMobileSmall }) => (isMobileSmall ? '100%' : '50%')};
  &:first-of-type {
    ${({ isMobileSmall }) => (isMobileSmall
    ? `margin-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`
    : `margin-right: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`)}
  }
`;

const CardWrapper = styled.div`
  margin-top: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
  margin-bottom: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
`;

/**
 * reducer state
 {
    query: {
      name?: string,
    }
 }
 */
function reducer(state: ReducerState, action: ActionType) {
  const { type, ...rest } = action;

  switch (type) {
  case 'setSelectedCompany': {
    return {
      ...state,
      company: action?.company,
    };
  }
  case 'setSelectedQuestion': {
    return {
      ...state,
      question: action?.question,
    };
  }
  case 'setDestructiveModal': {
    return {
      ...state,
      destructiveModal: action?.destructiveModal,
    };
  }
  case 'setNILThumbnail': {
    return {
      ...state,
      thumbnail: {
        ...action?.thumbnail,
        existingPhoto: {
          _id: action?.thumbnail?._id,
          uri: action?.thumbnail?.sources?.original?.url,
        },
      },
    };
  }
  case 'setState':
    return {
      ...state,
      ...rest,
    };
  default:
    return state;
  }
}

const initialReducerState: ReducerState = {
  fetching: false,
  loading: true,
  isSubmitting: false,
  company: undefined,
  question: undefined,
  thumbnail: {},
  imageUploadModal: {
    open: false,
  },
  discardModal: {
    open: false,
  },
  dirtyFields: {
    isDirty: false,
  },
  query: {},
};

const NILCreateScreen = (props: ManagedProps<Props>) => {
  const { viewport, history } = props;
  const { id: NILId } = useParams();

  const { notificationDispatch } = useNotification();
  const [state, dispatchReducer] = useReducer(reducer, initialReducerState);

  const isMobileSmall = viewport === 'mobileSmall';
  const isEdit = !!NILId;
  const goBackTitle = 'NIL Posts';

  // eslint-disable-next-line max-len
  const NILService = useSelector(({ NIL = {} }) => (NILId ? (NIL[NILId] || {}) : NIL?.newNIL), shallowEqual);
  const companiesService = useSelector(({ search }) => search?.GET_COMPANIES_API || {}, shallowEqual);
  const companyQuestionsService = useSelector(({ search }) => search?.GET_COMPANY_QUESTIONS_API || {}, shallowEqual);
  const imageService = useSelector(({ media = {} }) => media, shallowEqual);
  const notificationsService = useSelector(({ notifications = {} }) => notifications, shallowEqual);

  const dispatch: DispatchAction = useDispatch();

  /* Asset Utility functions using reducer and dispatch */
  const _clearCachedImage = () => {
    dispatch({
      type: ActionTypes.media.clearImage,
      mediaUploadReference: {
        uploadReferenceId: NILId || 'nilImage',
      },
    });
  };
  const _clearImageState = () => {
    dispatchReducer({
      type: 'setNILThumbnail',
      thumbnail: {},
    });
  };
  const _clearImage = () => {
    _clearCachedImage();
    _clearImageState();
  };

  useEffect(() => {
    // Clear image reducer on load to ensure cleanup occured
    _clearImage();
    if (isEdit) {
      dispatch({
        type: ActionTypes.NIL.get.START,
        id: NILId,
      });
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_edit_nil_post,
        properties: {
          referrer: 'NIL List',
        },
      });
    } else {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_add_new_nil_post,
        properties: {
          referrer: 'NIL List',
        },
      });
    }
  }, [isEdit]);

  useEffect(() => {
    /**
     * Only navigate the user back to the list if we're creating
     */
    if (NILId) return;
    if (notificationsService.variant === 'success'
    && notificationsService.content.indexOf('NIL') > 0
    && !!state.isSubmitting) {
      _clearImage();
      history.goBack();
    }
  }, [notificationsService]);

  useEffect(() => {
    if (!!NILService?.company && !state?.company) {
      dispatchReducer({
        type: 'setSelectedCompany',
        company: { text: NILService?.company?.name, ...NILService?.company },
      });
    }
  }, [NILService?.company?._id]);

  useEffect(() => {
    if (!!NILService?.question && !state?.question) {
      dispatchReducer({
        type: 'setSelectedQuestion',
        question: { text: NILService?.question?.question, ...NILService?.question },
      });
    }
  }, [NILService?.question?._id]);

  useEffect(() => {
    if ((state?.thumbnail?.uploadReferenceId && !!imageService[state?.thumbnail?.uploadReferenceId]?.assetId) || NILService?.thumbnail) {
      dispatchReducer({
        type: 'setNILThumbnail',
        thumbnail: {
          ...NILService?.thumbnail,
          ...imageService[state?.thumbnail?.uploadReferenceId],
        },
      });
    }
  }, [NILService?.thumbnail, imageService[state?.thumbnail?.uploadReferenceId]]);

  const _onVideoSearch = (value) => {
    dispatch({
      type: ActionTypes.search.getCompanyQuestions.START,
      searching: true,
      query: {
        q: value,
        featureTag: 'nil',
        page: 0,
      },
    });
  };

  const _onCompanySearch = (value) => {
    dispatch({
      type: ActionTypes.search.getCompanies.START,
      searching: true,
      query: {
        q: value,
        page: 0,
      },
    });
  };

  const INPUT_FIELDS = [
    'title',
    'description',
    'compensation',
    'endDate',
  ];

  const _onInputChange = (value, updatedInputName) => {
    dispatchReducer({
      type: 'setState',
      dirtyFields: {
        ...state?.dirtyFields,
        isDirty: true,
        [updatedInputName]: {
          value,
        },
      },
    });
  };

  const debouncedQuestions = debounce((e) => _onVideoSearch(e?.target?.value), 300);
  const debouncedCompanies = debounce((e) => _onCompanySearch(e?.target?.value), 300);
  const _debouncedInputChange = debounce((e, inputName) => _onInputChange(e?.target?.value, inputName), 100);

  const _updatedAltInputs = () => {
    const companyValue = state?.company?._id;
    const questionValue = state?.question?._id;
    const imageValue = state?.image?.assetId || NILService?.thumbnail?._id;

    if (companyValue !== NILService?.company?._id
      || questionValue !== NILService?.question?._id
      || imageValue !== NILService?.thumbnail?._id) {
      return true;
    }
    return false;
  };

  const _cancelNILPost = () => {
    if (NILService?.fetching || NILService?.isPatching || NILService?.posting) return;

    const dirtyFields = [];
    const updatedAltInputs = _updatedAltInputs();

    INPUT_FIELDS.forEach((field) => {
      if (!state?.dirtyFields[field]) return;
      console.log('field', field);
      /* Check if the field was changed, then changed back */
      let nilValue = NILService[field];
      let dirtyValue = state?.dirtyFields[field]?.value;
      if (field === 'endDate') dirtyValue = dateToUnixTimestamp(dirtyValue);
      if (field === 'compensation') nilValue = JSON.stringify(nilValue);
      if (dirtyValue && dirtyValue === nilValue) return;
      dirtyFields.push(field);
    });
    if ((dirtyFields?.length && dirtyFields?.length > 0) || updatedAltInputs) {
      dispatchReducer({
        type: 'setState',
        discardModal: {
          open: true,
        },
      });
      return;
    }
    _clearImage();
    history.goBack();
  };

  const _onSubmit = async (evt: React.SyntheticEvent) => {
    evt.preventDefault();
    if (NILService?.fetching || NILService?.isPatching) return;

    const target = evt?.target;
    const titleValue = target?.title?.value;
    const descriptionValue = target?.description?.value;
    const questionValue = state?.question?._id;
    const companyValue = state?.company?._id;
    const compensationValue = target?.compensation?.valueAsNumber;
    const endDateValue = dateToUnixTimestamp(target?.endDate?.value);
    const thumbnailValue = state?.thumbnail?.assetId || NILService?.thumbnail?._id;

    if (!titleValue || !descriptionValue || !questionValue || !compensationValue || !endDateValue) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Please fill out all required fields.',
        },
      });
      return;
    }

    if (!thumbnailValue) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Please upload a thumbnail image.',
        },
      });
      return;
    }

    dispatchReducer({
      type: 'setState',
      isSubmitting: true,
    });

    if (!NILId) {
      /**
       * Creating a new NIL!
       */
      dispatch({
        type: ActionTypes.NIL.post.START,
        body: {
          title: titleValue,
          description: descriptionValue,
          question: questionValue,
          company: companyValue,
          compensation: compensationValue,
          endDate: endDateValue,
          thumbnail: thumbnailValue,
        },
      });
    } else {
      /**
       * Editing an existing NIL!
       */

      const ops = [];

      if (titleValue !== NILService?.title) {
        ops.push({ op: 'add', path: 'title', value: titleValue });
      }

      if (descriptionValue !== NILService?.description) {
        ops.push({ op: 'add', path: 'description', value: descriptionValue });
      }

      if (questionValue !== NILService?.question?._id) {
        ops.push({ op: 'add', path: 'question', value: questionValue });
      }

      if (companyValue !== NILService?.company?._id) {
        ops.push({ op: 'add', path: 'company', value: companyValue });
      }

      if (!NILService?.compensation || compensationValue !== NILService?.compensation) {
        ops.push({ op: 'add', path: 'compensation', value: compensationValue });
      }

      if (!NILService?.endDate || endDateValue !== NILService?.endDate) {
        ops.push({ op: 'add', path: 'endDate', value: endDateValue });
      }

      if (thumbnailValue !== NILService?.thumbnail?._id) {
        ops.push({ op: 'add', path: 'thumbnail', value: thumbnailValue });
      }

      if (ops?.length < 1) {
        return dispatchReducer({
          type: 'setState',
          isSubmitting: false,
        });
      }

      dispatch({
        type: ActionTypes.NIL.patch.START,
        id: NILId,
        body: ops,
      });
    }
  };

  const _openImageModal = (uploadConfig: ImageUploadConfig): void => {
    const thumbnailState = {
      ...state?.thumbnail,
      ...uploadConfig,
    };
    dispatchReducer({
      type: 'setState',
      imageUploadModal: {
        open: true,
      },
    });
    dispatchReducer({
      type: 'setNILThumbnail',
      thumbnail: thumbnailState,
    });
  };

  const onClickDestructive = (type) => {
    if (type === 'clearImageCache') {
      /* Clear a stuck or unwanted upload */
      dispatch({
        type: ActionTypes.NIL.clearUploads,
        id: NILId,
      });
    } else {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.removed_nil_post_thumbnail,
      });
      dispatch({
        type: ActionTypes.NIL.patch.START,
        body: [
          {
            op: 'remove',
            path: 'thumbnail',
          },
        ],
        id: NILId,
      });
    }
    // Close modal
    _clearImage();
    dispatchReducer({
      type: 'setDestructiveModal',
      destructiveModal: {
        open: false,
        type: undefined,
      },
    });
  };

  const getImageId = (obj) => {
    dispatchReducer({
      type: 'setNILThumbnail',
      thumbnail: obj,
    });
  };

  const onWheel = (event: any): any => {
    event?.target?.blur();
  };

  const emptyCompanyQuestions = !companyQuestionsService?.results?.length;
  const emptyCompanies = !companiesService?.results?.length;

  const companyQuestionsOptions = !emptyCompanyQuestions
    ? companyQuestionsService?.results?.map((question) => ({ ...question, text: question?.question }))
    : [{ text: 'No Results' }];

  const companiesOptions = !emptyCompanies
    ? companiesService?.results?.map((company) => ({ ...company, text: company?.name }))
    : [{ text: 'No Results' }];

  const imageThumbnail = NILService?.thumbnail?.sources?.cropped?.url
  || NILService?.thumbnail?.sources?.original?.url
  || state?.thumbnail?.sources?.original?.url;

  const isLoadingImage = NILService?.fetching || imageService?.nilImage?.fetching || state?.thumbnail?.fetching;

  const menuOptionText = !isLoadingImage ? 'Remove image' : 'Cancel';
  const modalType = !isLoadingImage ? 'image' : 'clearImageCache';

  const imagePreviewLinks = [{
    children: menuOptionText,
    large: true,
    color: Colors.SecondaryRed,
    onClick: () => {
      dispatchReducer({
        type: 'setDestructiveModal',
        destructiveModal: {
          open: true,
          type: modalType,
        },
      });
    },
  },
  ];

  if (imageThumbnail && !isLoadingImage) {
    imagePreviewLinks.unshift({
      children: 'Edit image',
      large: true,
      onClick: () => {
        dispatch({
          type: ActionTypes.analytics.logEvent.START,
          name: eventNames.changed_nil_post_thumbnail,
        });
        _openImageModal({
          destination: 'nilImage',
          mediaUploadReferenceId: NILId ?? 'nilImage',
        });
      },
    });
  }

  return (
    <>
      <Helmet>
        <title>
          NIL
          {' '}
          { NILId ? 'Edit' : 'Create' }
          {' '}
          | ZIYNX
        </title>
      </Helmet>
      <ContentContainer>
        <UploadPhotoModal
          open={state?.imageUploadModal?.open}
          imageUploadConfig={state?.thumbnail}
          onClose={() => {
            dispatchReducer({
              type: 'setState',
              imageUploadModal: {
                open: false,
              },
            });
          }}
          getMediaId={getImageId}
        />
        <DestructiveModal
          heading={{
            text: `Are you sure you want to remove the ${state?.destructiveModal?.type}`,
          }}
          body={{
            text: 'This action cannot be undone.',
          }}
          open={state?.destructiveModal?.open}
          buttonGroup={{
            destructive: {
              children: 'Remove',
              onClick: () => onClickDestructive(state?.destructiveModal?.type),
            },
            primary: {
              children: 'Cancel',
              onClick: () => {
                dispatchReducer({
                  type: 'setDestructiveModal',
                  destructiveModal: {
                    open: false,
                    type: undefined,
                  },
                });
              },
            },
          }}
        />
        <PageWrapper isMobileSmall={isMobileSmall}>
          <PageContentWrapper>
            <LinkWrapper>
              <Link
                variant="caret"
                onClick={_cancelNILPost}
                color={Colors.GrayDark}
              >
                Back to NIL
              </Link>
            </LinkWrapper>
            { (NILService?.fetching) && (<LoadingWrapper><LoadingSpinner /></LoadingWrapper>) }
            { (!NILService?.fetching) && (
              <Form
                formId="NIL-form"
                title="Customize NIL Post"
                width="100%"
                noVaidate
                onSubmit={_onSubmit}
              >
                <fieldset disabled={state?.isSubmitting}>
                  <FormRow>
                    <Input
                      required
                      disabled={NILService?.isPatching || NILService?.fetching}
                      variant="text"
                      autoComplete="off"
                      inputName="title"
                      label="NIL Title"
                      labelColor={Colors.GrayDark}
                      onChange={(e) => _debouncedInputChange(e, 'title')}
                      defaultValue={NILService?.title ?? ''}
                    />
                  </FormRow>
                  <FormRow paddingBottom="66px">
                    <Input
                      required
                      variant="textarea"
                      autoComplete="off"
                      inputName="description"
                      label="Description"
                      labelColor={Colors.GrayDark}
                      disabled={NILService?.fetching || NILService?.isPatching}
                      onChange={(e) => _debouncedInputChange(e, 'description')}
                      defaultValue={NILService?.description ?? ''}
                    />
                  </FormRow>
                  <FormRow>
                    <Input
                      required
                      disabled={NILService?.isPatching || NILService?.fetching}
                      variant="text"
                      helperText="Ex. 08/01/2023"
                      autoComplete="off"
                      inputName="endDate"
                      label="End Date"
                      labelColor={Colors.GrayDark}
                      onChange={(e) => _debouncedInputChange(e, 'endDate')}
                      defaultValue={NILService?.endDate ? unixTimestampToDate(NILService?.endDate) : undefined}
                    />
                  </FormRow>
                  <FormRow>
                    <Input
                      required
                      disabled={NILService?.isPatching || NILService?.fetching}
                      variant="number"
                      autoComplete="off"
                      inputName="compensation"
                      label="Compenstation"
                      labelColor={Colors.GrayDark}
                      onChange={(e) => _debouncedInputChange(e, 'compensation')}
                      defaultValue={NILService?.compensation ?? ''}
                      onWheel={onWheel}
                    />
                  </FormRow>
                  <FormRow paddingBottom="16px" height="100%">
                    {/* DOn't make this required */}
                    <InputAutocomplete
                      inputName="question"
                      label="Select NIL video question"
                      width="auto"
                      showOptionsOnFocus
                      disabled={companyQuestionsService?.fetching || NILService?.isPatching}
                      isLoading={!!companyQuestionsService?.loading}
                      isPaginating={!emptyCompanyQuestions && (companyQuestionsService?.paginating || companyQuestionsService?.searching)}
                      onChange={debouncedQuestions}
                      onFocus={debouncedQuestions}
                      options={companyQuestionsOptions}
                      onClick={(e, option) => {
                        if (!emptyCompanyQuestions) {
                          dispatchReducer({
                            type: 'setSelectedQuestion',
                            question: option,
                          });
                        }
                      }}
                      selected={state?.question ? [state?.question] : undefined}
                      onSelectedClick={state?.isSubmitting ? () => {}
                        : () => {
                        /**
                         * Remove question
                         */
                          dispatchReducer({
                            type: 'setSelectedQuestion',
                            question: undefined,
                          });
                        }}
                    />
                  </FormRow>
                  <FormRow paddingBottom="16px" height="100%">
                    <InputAutocomplete
                    // DOn't make this required
                      inputName="company"
                      label="Select Company"
                      labelColor={Colors.GrayDark}
                      width="auto"
                      showOptionsOnFocus
                      disabled={companiesService?.fetching || NILService?.isPatching}
                      isLoading={!!companiesService?.loading}
                      isPaginating={!emptyCompanies && (companiesService?.paginating || companiesService?.searching)}
                      onChange={debouncedCompanies}
                      onFocus={debouncedCompanies}
                      options={companiesOptions}
                      onClick={(e, option) => {
                        if (!emptyCompanies) {
                          dispatchReducer({
                            type: 'setSelectedCompany',
                            company: option,
                          });
                        }
                      }}
                      selected={state?.company ? [state?.company] : undefined}
                      onSelectedClick={state?.isSubmitting ? () => {}
                        : () => {
                        /**
                         * Remove company
                         */
                          dispatchReducer({
                            type: 'setSelectedCompany',
                            company: undefined,
                          });
                        }}
                    />
                  </FormRow>
                  <CardWrapper>
                    <Card
                      variant="videoPreview"
                      title="Image"
                      isLoading={isLoadingImage}
                      showMenuOnLoad
                      image={{ imageSource: imageThumbnail }}
                      link={{
                        children: 'Add a thumbnail image',
                        onClick: () => {
                          dispatch({
                            type: ActionTypes.analytics.logEvent.START,
                            name: eventNames.added_nil_post_thumbnail,
                          });
                          _openImageModal({
                            destination: 'nilImage',
                            mediaUploadReferenceId: NILId ?? 'nilImage',
                          });
                        },
                        disabled: state?.isSubmitting,
                      }}
                      rightAction={{
                        popover: {
                          position: 'bottomLeft',
                          links: imagePreviewLinks,
                        },
                        disabled: state?.isSubmitting,
                      }}
                    />
                  </CardWrapper>
                  <ButtonWrapper isMobileSmall={isMobileSmall}>
                    <StyledButton
                      type="submit"
                      width="50%"
                      disabled={NILService?.fetching || NILService?.isPatching || state?.isSubmitting}
                      isLoading={NILService?.isPatching || NILService?.isPatching || NILService?.posting}
                      isMobileSmall={isMobileSmall}
                    >
                      Save
                    </StyledButton>
                    <StyledButton
                      isMobileSmall={isMobileSmall}
                      disabled={state?.isSubmitting}
                      variant="primary"
                      width="50%"
                      inverted
                      onClick={_cancelNILPost}
                    >
                      Cancel
                    </StyledButton>
                  </ButtonWrapper>
                </fieldset>
              </Form>
            )}
          </PageContentWrapper>
        </PageWrapper>
        <DiscardModal open={state?.discardModal?.open} viewport={viewport}>
          <ModalContent>
            <StyledHeading small>Are you sure you want to discard your changes?</StyledHeading>
            <ModalButtonWrapper isMobileSmall={isMobileSmall}>
              <ModalButton
                width="100%"
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                firstChild
                onClick={() => {
                  history.goBack();
                }}
              >
                Back to
                {' '}
                {goBackTitle}

              </ModalButton>
              <ModalButton
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                inverted
                width="100%"
                onClick={() => {
                  dispatchReducer({
                    type: 'setState',
                    discardModal: {
                      open: false,
                    },
                  });
                }}
              >
                Cancel
              </ModalButton>
            </ModalButtonWrapper>
          </ModalContent>
        </DiscardModal>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(NILCreateScreen): React$ComponentType<ManagedProps<Props>>);
